import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { packetsApi, bullhornApi, templatesApi, settingsApi, getErrorMessage, resolveBackendUrl } from '../services/api';
import { useLinkExpiry, linkExpiryOptions } from '../utils/linkExpiry';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Package,
  User,
  Building2,
  Send,
  RefreshCw,
  Search,
  FileText,
  CheckCircle2,
  Eye,
  ExternalLink,
  AlertCircle,
  Timer
} from 'lucide-react';
import debounce from 'lodash/debounce';
import { formatEstimate, formatPacketEstimate } from '../utils/duration';
import { useSenderFillableFields } from '../hooks/useSenderFillableFields';
import SignatureCaptureDialog from '../components/send/SignatureCaptureDialog';
import SenderFieldsPanel from '../components/send/SenderFieldsPanel';

// Where a template actually delivers, mirroring the worker's own routing
// (packets.ts targetsFor) and BullhornEmbedPage.js's identical helper: a
// candidate/contact-typed template routes by its own entity_type; a
// placement-typed template routes by its send_to_candidate/send_to_contact
// flags (and may go to both). Shared by the "Send to" toggle, the routing
// preview, and the deliverable-count so none of them can disagree.
function templateRouting(t) {
  const entity = (t.entity_type || 'candidate').toLowerCase();
  const sendToCandidate = entity === 'placement' ? !!t.send_to_candidate : entity !== 'contact';
  const sendToContact = entity === 'placement' ? !!t.send_to_contact : entity === 'contact';
  return { sendToCandidate, sendToContact };
}
function templateAllowsSide(t, side) {
  const { sendToCandidate, sendToContact } = templateRouting(t);
  return side === 'contact' ? sendToContact : sendToCandidate;
}

// Resolve, for a placement packet, exactly which documents will actually be
// sent and to whom under the current "Send to" choice. "both" (the default,
// matching behavior from before this toggle existed) leaves each
// template's own send_to_candidate/send_to_contact routing untouched;
// picking Candidate or Contact additionally restricts every template to
// that one side. Shared by the routing preview list and the Send button's
// count/disabled state.
function resolvePlacementRouting(templatesList, placement, sendTarget) {
  return (templatesList || []).map((t) => {
    const { sendToCandidate, sendToContact } = templateRouting(t);
    const allowCandidate = sendToCandidate && (sendTarget === 'both' || sendTarget === 'candidate');
    const allowContact = sendToContact && (sendTarget === 'both' || sendTarget === 'contact');
    if (!allowCandidate && !allowContact) {
      return {
        template: t,
        applicable: false,
        reason: sendTarget === 'both'
          ? "not part of this packet's routing"
          : `only sends to ${sendToContact ? 'contact' : 'candidate'}`,
      };
    }
    const isContactOnly = allowContact && !allowCandidate;
    const missing = isContactOnly ? !placement?.contact?.email : !placement?.candidate?.email;
    const reason = isContactOnly
      ? (!placement?.contact?.id ? 'no contact' : 'no email')
      : (!placement?.candidate?.id ? 'no candidate' : 'no email');
    const label = allowCandidate && allowContact ? 'Both' : isContactOnly ? 'Contact' : 'Candidate';
    return { template: t, applicable: !missing, missing, reason, label, isContactOnly };
  });
}

export default function SendPacketPage() {
  const navigate = useNavigate();
  const { getTenantPath } = useAuth();
  const [packets, setPackets] = useState([]);
  const [selectedPacket, setSelectedPacket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [bullhornConnected, setBullhornConnected] = useState(false);
  // Full templates list (each with its `fields` array) — fetched once so
  // Requester/Admin (sender_fillable) fields on the packet's templates can
  // be resolved without a per-packet backend round trip.
  const [allTemplates, setAllTemplates] = useState([]);

  // Recipient search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  // "Or enter manually" — a recipient typed in by name + email instead of
  // picked from Bullhorn (candidate/contact packets only).
  const [manualName, setManualName] = useState('');
  const [manualEmail, setManualEmail] = useState('');

  // Placement-mode state (only used when packet.entity_type === 'placement').
  // Holds the chosen placement record with its resolved candidate + contact
  // so we can route each template in the packet to the right party.
  const [selectedPlacement, setSelectedPlacement] = useState(null);
  // Which side(s) of a placement to actually send to this time —
  // 'candidate' | 'contact' | 'both'. Defaults to 'both', which matches
  // every template's own send_to_candidate/send_to_contact routing exactly
  // as before this toggle existed, so leaving it untouched changes nothing.
  const [sendTarget, setSendTarget] = useState('both');

  // Send options
  const { expiryHours, setExpiryHours } = useLinkExpiry(settingsApi.getLinkExpiry);
  const [requiresApproval, setRequiresApproval] = useState(false);
  
  // Success state
  const [sendResult, setSendResult] = useState(null);
  // Test-preview dry run state — kept separate from the live send state
  // so the recruiter can preview as many times as they want without
  // affecting the Send button's spinner.
  const [previewing, setPreviewing] = useState(false);
  const [previewResults, setPreviewResults] = useState(null); // null | array
  // "Preview all documents" modal — opens once a test preview renders, so
  // the sender can flip through every document in the packet as its actual
  // template with mapped values overlaid, not a bare list of download links.
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [selectedPreviewIndex, setSelectedPreviewIndex] = useState(0);

  useEffect(() => {
    loadPackets();
    checkBullhornStatus();
    loadTemplates();
  }, []);

  const loadPackets = async () => {
    try {
      const response = await packetsApi.list();
      setPackets(response.data);
    } catch (error) {
      toast.error('Failed to load packets');
    } finally {
      setLoading(false);
    }
  };

  const checkBullhornStatus = async () => {
    try {
      const response = await bullhornApi.status();
      setBullhornConnected(response.data.connected);
    } catch (error) {
      setBullhornConnected(false);
    }
  };

  const loadTemplates = async () => {
    try {
      const response = await templatesApi.list();
      setAllTemplates(response.data);
    } catch (error) {
      // Non-fatal — the Requester-fields panel just won't have anything
      // to show; packet selection and sending are otherwise unaffected.
    }
  };

  // Match the selected packet's (minimal) template list against the full
  // templates list to get each one's `fields` array.
  const activeTemplatesForPacket = (selectedPacket?.templates || [])
    .map((t) => allTemplates.find((at) => at.id === t.id))
    .filter(Boolean);

  const {
    senderFieldValues, setSenderFieldValues, senderFieldKey,
    signingKey, setSigningKey,
    signatureMode, setSignatureMode,
    typedSignature, setTypedSignature,
    signaturePadRef, initSignatureCanvas, saveSignature,
    senderFillableFields, unfilledSenderFields, buildPrefilledValues,
  } = useSenderFillableFields(activeTemplatesForPacket);

  // Which side(s) the packet's own templates actually support. "Both" is only
  // a real choice when the packet needs both sides — a packet of only
  // candidate documents has nothing to send to a contact, so "Both" would
  // just be "Candidate" under another name.
  const packetTemplates = selectedPacket?.templates || [];
  const canSendToCandidateSide = packetTemplates.length === 0
    || packetTemplates.some((t) => templateAllowsSide(t, 'candidate'));
  const canSendToContactSide = packetTemplates.length === 0
    || packetTemplates.some((t) => templateAllowsSide(t, 'contact'));
  const canSendToBothSides = canSendToCandidateSide && canSendToContactSide;
  // wireTarget is what actually drives routing/payloads. When the packet only
  // has one side there's no choice to make, so it stays 'both' — the exact
  // original behavior and request body, untouched. displayTarget is what the
  // toggle highlights, which for a one-sided packet is that one side.
  const wireTarget = canSendToBothSides ? sendTarget : 'both';
  const displayTarget = canSendToBothSides
    ? sendTarget
    : canSendToCandidateSide ? 'candidate' : canSendToContactSide ? 'contact' : 'both';

  // Debounced search — covers candidate, contact, and placement modes.
  const performSearch = useCallback(
    debounce(async (query, entityType) => {
      if (!query || query.length < 2) {
        setSearchResults([]);
        return;
      }

      setSearching(true);
      try {
        let results = [];
        if (entityType === 'placement') {
          const r = await bullhornApi.searchPlacements(query);
          results = r.data?.placements || [];
        } else if (entityType === 'contact') {
          const r = await bullhornApi.searchContacts(query);
          results = r.data?.contacts || [];
        } else {
          const r = await bullhornApi.searchCandidates(query);
          results = r.data?.candidates || [];
        }
        setSearchResults(results);
      } catch (error) {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300),
    []
  );

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    if (selectedPacket) {
      performSearch(query, selectedPacket.entity_type);
    }
  };

  const handlePacketSelect = (packetId) => {
    const packet = packets.find(p => p.id === packetId);
    setSelectedPacket(packet);
    setSelectedRecipient(null);
    setSelectedPlacement(null);
    setSendTarget('both');
    setSearchQuery('');
    setSearchResults([]);
  };

  // Single-recipient pick (candidate/contact mode).
  const handleRecipientSelect = (recipient) => {
    setSelectedRecipient(recipient);
    setSelectedPlacement(null);
    setSearchQuery('');
    setSearchResults([]);
  };

  // Manually typed recipient — no Bullhorn record behind it, so no Bullhorn
  // id (the send then just skips anything Bullhorn-prefilled for this person).
  const manualEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(manualEmail.trim());
  const handleManualRecipient = () => {
    if (!manualName.trim() || !manualEmailValid) return;
    handleRecipientSelect({ id: null, name: manualName.trim(), email: manualEmail.trim(), manual: true });
  };

  // Placement pick — surfaces the resolved candidate + contact so the
  // recruiter can confirm both parties before sending. The actual routing
  // (which template goes to whom) happens server-side.
  const handlePlacementSelect = (placement) => {
    setSelectedPlacement(placement);
    setSelectedRecipient(null);
    setSendTarget('both');
    setSearchQuery('');
    setSearchResults([]);
  };

  // The unconditional "route each template to whichever side(s) its own
  // send_to_candidate/send_to_contact flags allow" placement payload — this
  // is the real send body for "Both" (the default, unchanged from before
  // the "Send to" toggle existed) AND what we always ask /test-preview for
  // regardless of the current choice, filtering client-side afterward — see
  // handleTestPreview for why.
  const buildFullPlacementPayload = () => {
    const cand = selectedPlacement.candidate;
    const cont = selectedPlacement.contact;
    return {
      placement: {
        placement_id: selectedPlacement.id,
        candidate: cand && cand.id
          ? { name: cand.name || '', email: cand.email || '', bullhorn_id: cand.id, entity_type: 'candidate' }
          : null,
        contact: cont && cont.id
          ? {
              name: cont.name || '', email: cont.email || '', bullhorn_id: cont.id,
              entity_type: 'contact',
              company_id: selectedPlacement.company?.id || null,
              company_name: selectedPlacement.company?.name || null,
            }
          : null,
        company: selectedPlacement.company
          ? { bullhorn_id: selectedPlacement.company.id, name: selectedPlacement.company.name }
          : null,
      },
    };
  };

  // Both handleSend and handleTestPreview need the exact same payload
  // shape (so a preview reflects what the real send would do). Extract
  // the build logic into a single helper to keep them in lock-step.
  const buildSendPayload = () => {
    const isPlacementMode = selectedPacket.entity_type === 'placement';
    const payload = {
      packet_id: selectedPacket.id,
      expiry_hours: expiryHours,
      requires_approval: requiresApproval,
    };
    if (isPlacementMode) {
      // Always the full candidate+contact placement body — each template is
      // routed by its own entity type server-side, and prefill still sees
      // both parties. A Candidate-only / Contact-only choice only adds
      // recipient_target, which narrows the targets after that routing.
      Object.assign(payload, buildFullPlacementPayload());
      if (wireTarget === 'candidate' || wireTarget === 'contact') {
        payload.recipient_target = wireTarget;
      }
    } else {
      const recipientName = selectedRecipient.name ||
        `${selectedRecipient.firstName || ''} ${selectedRecipient.lastName || ''}`.trim();
      payload.recipient = {
        name: recipientName,
        email: selectedRecipient.email,
        bullhorn_id: selectedRecipient.id,
        entity_type: selectedPacket.entity_type,
        title: selectedRecipient.occupation || selectedRecipient.title || null,
        company_id: selectedRecipient.company?.id || null,
        company_name: selectedRecipient.company?.name || null,
      };
    }
    const prefilledValues = buildPrefilledValues((selectedPacket.templates || []).map((t) => t.id));
    if (Object.keys(prefilledValues).length > 0) {
      payload.prefilled_values = prefilledValues;
    }
    return payload;
  };

  // Dry-run: render preview PDFs without creating documents or sending
  // emails. Lets the recruiter validate field mappings against a real
  // Bullhorn record before committing to a live candidate send.
  const handleTestPreview = async () => {
    if (!selectedPacket) {
      toast.error('Please select a packet');
      return;
    }
    const isPlacementMode = selectedPacket.entity_type === 'placement';
    if (isPlacementMode && !selectedPlacement) {
      toast.error('Please select a placement');
      return;
    }
    if (!isPlacementMode && !selectedRecipient) {
      toast.error('Please select a recipient');
      return;
    }
    setPreviewing(true);
    setPreviewResults(null);
    try {
      let payload;
      if (isPlacementMode) {
        // Always request the full candidate+contact routing regardless of
        // the current "Send to" choice, then filter client-side below —
        // unlike /send, /test-preview doesn't filter a single-recipient
        // request by the template's own send_to_candidate/send_to_contact
        // flags, so asking it for just one side directly could render a
        // document that isn't even set up for that side. Mirrors
        // BullhornEmbedPage.js's identical preview workaround.
        payload = { packet_id: selectedPacket.id, ...buildFullPlacementPayload() };
        const prefilledValues = buildPrefilledValues((selectedPacket.templates || []).map((t) => t.id));
        if (Object.keys(prefilledValues).length > 0) payload.prefilled_values = prefilledValues;
      } else {
        payload = buildSendPayload();
      }
      const res = await packetsApi.testPreview(payload);
      let previews = res.data?.previews || [];
      if (isPlacementMode && wireTarget !== 'both') {
        previews = previews.filter((p) => p.route === wireTarget);
      }
      setPreviewResults(previews);
      setSelectedPreviewIndex(0);
      if (previews.length > 0) {
        setPreviewModalOpen(true);
      } else {
        toast.error('No documents rendered');
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Preview render failed'));
    } finally {
      setPreviewing(false);
    }
  };

  const handleSend = async () => {
    if (!selectedPacket) {
      toast.error('Please select a packet');
      return;
    }
    const isPlacementMode = selectedPacket.entity_type === 'placement';
    if (isPlacementMode && !selectedPlacement) {
      toast.error('Please select a placement');
      return;
    }
    if (!isPlacementMode && !selectedRecipient) {
      toast.error('Please select a recipient');
      return;
    }
    if (isPlacementMode) {
      const deliverable = resolvePlacementRouting(selectedPacket.templates, selectedPlacement, wireTarget)
        .filter((r) => r.applicable).length;
      if (deliverable === 0) {
        toast.error('No documents are deliverable to the selected recipient for this "Send to" choice');
        return;
      }
    }
    if (unfilledSenderFields.length > 0) {
      toast.error(`Please fill in before sending: ${unfilledSenderFields.map((f) => f.field.label).join(', ')}`);
      return;
    }

    setSending(true);
    try {
      const payload = buildSendPayload();

      const response = await packetsApi.send(payload);
      setSendResult(response.data);
      const recipientCount = (response.data?.recipients || []).length;
      const skipped = response.data?.skipped || [];
      if (skipped.length > 0) {
        toast.success(
          `Sent to ${recipientCount} recipient(s). ${skipped.length} document(s) weren't applicable to this recipient — ${skipped[0].reason}.`
        );
      } else {
        toast.success('Packet sent successfully!');
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to send packet'));
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  // Success state
  if (sendResult) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto">
          <div className="neumorphic rounded-xl p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="font-heading text-2xl font-bold mb-2">Packet Sent Successfully!</h2>
            <p className="text-muted-foreground mb-6">
              {sendResult.documents_created} document(s) delivered
              {sendResult.recipients?.length > 1
                ? ` across ${sendResult.recipients.length} recipients.`
                : sendResult.recipients?.[0]
                  ? ` to ${sendResult.recipients[0].email}`
                  : sendResult.recipient
                    ? ` to ${sendResult.recipient}`
                    : ''}
            </p>

            {/* Per-recipient breakdown — only meaningful in placement mode */}
            {sendResult.recipients?.length > 1 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4 text-left">
                <p className="font-medium mb-2 text-blue-900 text-sm">Routed to:</p>
                <ul className="space-y-2">
                  {sendResult.recipients.map((r) => (
                    <li
                      key={`${r.route}-${r.email || r.name}`}
                      className="flex items-center gap-2 text-sm text-blue-900"
                    >
                      {r.route === 'contact' ? (
                        <Building2 className="w-4 h-4" />
                      ) : (
                        <User className="w-4 h-4" />
                      )}
                      <span className="font-medium">{r.name}</span>
                      <span className="text-blue-700">· {r.email}</span>
                      <span className="ml-auto text-xs text-blue-700">{r.documents} doc(s)</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {sendResult.skipped?.length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4 text-left" data-testid="send-result-skipped">
                <p className="font-medium mb-2 text-blue-900 text-sm">Not sent to this recipient:</p>
                <ul className="space-y-1 text-sm text-blue-800">
                  {sendResult.skipped.map((s) => (
                    <li key={`${s.template_id || s.template_name}-${s.reason}`}>
                      <span className="font-medium">{s.template_name}</span>
                      <span className="text-blue-700"> · {s.reason}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="bg-muted/30 rounded-lg p-4 mb-6 text-left">
              <p className="font-medium mb-2">Documents sent:</p>
              <ul className="space-y-2">
                {sendResult.documents?.map((doc, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm">
                    <FileText className="w-4 h-4 text-muted-foreground" />
                    <span>{doc.template_name}</span>
                    {doc.route && (
                      <span className="text-xs text-muted-foreground ml-auto">to {doc.route}</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex gap-3 justify-center">
              <Button variant="outline" onClick={() => {
                setSendResult(null);
                setSelectedPacket(null);
                setSelectedRecipient(null);
                setSelectedPlacement(null);
              }}>
                Send Another
              </Button>
              <Button onClick={() => navigate(getTenantPath('/documents'))}>
                View Documents
              </Button>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Exactly what the current "Send to" choice resolves to for this
  // placement — drives both the routing preview list and the Send button's
  // document count/disabled state, so they can never disagree.
  const placementRouting = selectedPlacement
    ? resolvePlacementRouting(selectedPacket?.templates, selectedPlacement, wireTarget)
    : [];
  const placementDeliverableCount = placementRouting.filter((r) => r.applicable).length;

  // Requester/Admin (sender_fillable) fields panel — shown above the Send
  // button whenever the selected packet has any. Shared between the
  // placement-mode and single-recipient-mode branches below.
  const senderFieldsPanel = (
    <SenderFieldsPanel
      senderFillableFields={senderFillableFields}
      senderFieldValues={senderFieldValues}
      setSenderFieldValues={setSenderFieldValues}
      senderFieldKey={senderFieldKey}
      setSigningKey={setSigningKey}
      testIdPrefix="packet-sender-fillable"
    />
  );

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="send-packet-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(getTenantPath('/send'))}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-heading text-2xl font-bold text-foreground">Send Packet</h1>
            <p className="text-muted-foreground font-body">
              Send a collection of documents to a candidate or contact
            </p>
          </div>
        </div>

        {/* Warning if no Bullhorn connection */}
        {!bullhornConnected && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-amber-800">Bullhorn not connected</p>
              <p className="text-sm text-amber-700">
                Connect to Bullhorn in Settings to search for candidates/contacts
              </p>
            </div>
          </div>
        )}

        {packets.length === 0 ? (
          <div className="neumorphic rounded-xl p-12 text-center">
            <Package className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="font-heading font-semibold text-lg mb-2">No packets available</h3>
            <p className="text-muted-foreground font-body mb-6">
              Create a packet first in the Templates section
            </p>
            <Button onClick={() => navigate(getTenantPath('/templates'))}>
              Go to Templates
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column - Packet Selection */}
            <div className="space-y-6">
              <div className="neumorphic rounded-xl p-6">
                <h3 className="font-heading font-semibold mb-4 flex items-center gap-2">
                  <Package className="w-5 h-5 text-purple-600" />
                  Select Packet
                </h3>
                
                <Select value={selectedPacket?.id || ''} onValueChange={handlePacketSelect}>
                  <SelectTrigger data-testid="packet-select">
                    <SelectValue placeholder="Choose a packet..." />
                  </SelectTrigger>
                  <SelectContent>
                    {packets.map(packet => (
                      <SelectItem key={packet.id} value={packet.id}>
                        <div className="flex items-center gap-2">
                          <span>{packet.name}</span>
                          <span className="text-xs text-muted-foreground">
                            ({packet.template_ids?.length || 0} docs)
                          </span>
                          {formatPacketEstimate(packet.templates) && (
                            <span className="text-xs text-muted-foreground">· {formatPacketEstimate(packet.templates)}</span>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {selectedPacket && (
                  <div className="mt-4 space-y-3">
                    <div className="flex items-center gap-2">
                      {selectedPacket.entity_type === 'placement' ? (
                        <Package className="w-4 h-4 text-purple-600" />
                      ) : selectedPacket.entity_type === 'contact' ? (
                        <Building2 className="w-4 h-4 text-[#F9A825]" />
                      ) : (
                        <User className="w-4 h-4 text-[#4CAF50]" />
                      )}
                      <span className="text-sm capitalize">{selectedPacket.entity_type} packet</span>
                    </div>
                    {selectedPacket.description && (
                      <p className="text-sm text-muted-foreground">{selectedPacket.description}</p>
                    )}
                    <div className="border-t pt-3">
                      <p className="text-xs text-muted-foreground mb-2">Documents in this packet:</p>
                      <div className="space-y-1">
                        {selectedPacket.templates?.map(t => (
                          <div key={t.id} className="flex items-center gap-2 text-sm">
                            <FileText className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                            <span className="min-w-0 flex-1 truncate">{t.name}</span>
                            {formatEstimate(t.estimated_seconds) && (
                              <span className="text-xs text-muted-foreground flex-shrink-0 whitespace-nowrap" data-testid={`packet-doc-estimate-${t.id}`}>
                                {formatEstimate(t.estimated_seconds)}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                      {formatPacketEstimate(selectedPacket.templates) && (
                        <p className="mt-2 flex items-center gap-1.5 text-xs font-medium text-foreground" data-testid="packet-total-estimate">
                          <Timer className="w-3.5 h-3.5 text-muted-foreground" />
                          Estimated time for the candidate: {formatPacketEstimate(selectedPacket.templates)}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Send Options */}
              <div className="neumorphic rounded-xl p-6">
                <h3 className="font-heading font-semibold mb-4">Send Options</h3>
                
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Expiry Time</Label>
                    <Select value={expiryHours.toString()} onValueChange={(v) => setExpiryHours(parseInt(v))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {linkExpiryOptions(expiryHours).map((o) => (
                          <SelectItem key={o.hours} value={String(o.hours)}>{o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="approval" 
                      checked={requiresApproval}
                      onCheckedChange={setRequiresApproval}
                    />
                    <Label htmlFor="approval" className="cursor-pointer">
                      Require admin approval before syncing to Bullhorn
                    </Label>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Recipient Selection */}
            <div className="neumorphic rounded-xl p-6">
              <h3 className="font-heading font-semibold mb-4 flex items-center gap-2">
                {selectedPacket?.entity_type === 'placement' ? (
                  <Package className="w-5 h-5 text-purple-600" />
                ) : selectedPacket?.entity_type === 'contact' ? (
                  <Building2 className="w-5 h-5 text-[#F9A825]" />
                ) : (
                  <User className="w-5 h-5 text-[#4CAF50]" />
                )}
                {selectedPacket?.entity_type === 'placement' ? 'Select Placement' : 'Select Recipient'}
              </h3>

              {!selectedPacket ? (
                <p className="text-muted-foreground text-sm">Select a packet first</p>
              ) : selectedPlacement ? (
                /* Placement-mode: show resolved candidate + contact so the
                   recruiter sees exactly who each document will go to. */
                <div className="space-y-4">
                  <div className="bg-purple-50/60 border border-purple-200 rounded-lg p-4 space-y-3" data-testid="placement-resolved-card">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 text-xs uppercase tracking-wide font-semibold text-purple-700">
                          <Package className="w-3.5 h-3.5" />
                          Placement #{selectedPlacement.id}
                        </div>
                        {selectedPlacement.jobOrder?.title && (
                          <p className="text-sm font-medium mt-0.5">{selectedPlacement.jobOrder.title}</p>
                        )}
                        {selectedPlacement.company?.name && (
                          <p className="text-xs text-muted-foreground">{selectedPlacement.company.name}</p>
                        )}
                      </div>
                      <Button variant="outline" size="sm" onClick={() => setSelectedPlacement(null)} data-testid="placement-change-button">
                        Change
                      </Button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-2 border-t border-purple-200">
                      <div className={`rounded-md p-2.5 text-sm ${selectedPlacement.candidate?.email ? 'bg-white border border-emerald-200' : 'bg-amber-50 border border-amber-200'}`} data-testid="placement-candidate-block">
                        <div className="flex items-center gap-1.5 text-xs font-semibold text-emerald-800 mb-1">
                          <User className="w-3 h-3" /> Candidate
                        </div>
                        {selectedPlacement.candidate?.email ? (
                          <>
                            <p className="font-medium truncate">{selectedPlacement.candidate.name}</p>
                            <p className="text-xs text-muted-foreground truncate">{selectedPlacement.candidate.email}</p>
                          </>
                        ) : (
                          <p className="text-xs text-amber-800">No candidate on this placement</p>
                        )}
                      </div>
                      <div className={`rounded-md p-2.5 text-sm ${selectedPlacement.contact?.email ? 'bg-white border border-amber-200' : 'bg-amber-50 border border-amber-200'}`} data-testid="placement-contact-block">
                        <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-800 mb-1">
                          <Building2 className="w-3 h-3" /> Contact
                        </div>
                        {selectedPlacement.contact?.email ? (
                          <>
                            <p className="font-medium truncate">{selectedPlacement.contact.name}</p>
                            <p className="text-xs text-muted-foreground truncate">{selectedPlacement.contact.email}</p>
                          </>
                        ) : selectedPlacement.contact?.id ? (
                          /* Contact exists on the placement but has no email
                             on file in Bullhorn — we can't deliver so the
                             recruiter needs to update the contact record. */
                          <>
                            <p className="font-medium truncate">{selectedPlacement.contact.name || `Contact #${selectedPlacement.contact.id}`}</p>
                            <p className="text-xs text-amber-800 mt-0.5">
                              No email address on Bullhorn — update the contact record to send.
                            </p>
                          </>
                        ) : (
                          <p className="text-xs text-amber-800">No contact on this placement</p>
                        )}
                      </div>
                    </div>

                    {/* Routing preview — show which templates will go to whom
                        under the current "Send to" choice below */}
                    {placementRouting.length > 0 && (
                      <div className="pt-2 border-t border-purple-200">
                        <p className="text-xs font-semibold text-purple-700 mb-1.5">Routing preview:</p>
                        <ul className="space-y-1 text-xs">
                          {placementRouting.map(({ template: t, applicable, reason, label, isContactOnly }) => (
                            <li key={t.id} className="flex items-center gap-1.5">
                              {!applicable ? (
                                <AlertCircle className="w-3 h-3 text-amber-600" />
                              ) : isContactOnly ? (
                                <Building2 className="w-3 h-3 text-[#F9A825]" />
                              ) : (
                                <User className="w-3 h-3 text-[#4CAF50]" />
                              )}
                              <span className="truncate">{t.name}</span>
                              <span className="ml-auto text-muted-foreground">
                                {applicable ? `to ${label.toLowerCase()}` : `skipped — ${reason}`}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Send to — defaults to Both when the packet has documents
                      for both sides (every document routes exactly as its
                      template defines, unchanged); picking Candidate or
                      Contact restricts the packet to that one side. A packet
                      with documents for only one side shows that side
                      selected and "Both" disabled. Mirrors the equivalent
                      toggle in the Bullhorn-embedded HyreDocs tab. */}
                  <div className="space-y-1.5" data-testid="packet-send-target-toggle">
                    <Label className="text-xs">Send to</Label>
                    <div className="grid grid-cols-3 gap-1.5 bg-muted/40 rounded-lg p-1">
                      <button
                        type="button"
                        onClick={() => setSendTarget('candidate')}
                        disabled={!canSendToCandidateSide || !selectedPlacement.candidate?.email}
                        className={`text-xs px-2 py-2 rounded-md transition-colors ${
                          displayTarget === 'candidate'
                            ? 'bg-purple-600 text-white shadow-sm'
                            : canSendToCandidateSide && selectedPlacement.candidate?.email
                              ? 'text-foreground hover:bg-white'
                              : 'text-muted-foreground/50 cursor-not-allowed'
                        }`}
                        data-testid="send-target-candidate"
                        title={
                          !selectedPlacement.candidate?.email ? 'No candidate email on this placement'
                            : !canSendToCandidateSide ? "This packet isn't set up to send to the candidate"
                              : undefined
                        }
                      >
                        Candidate
                      </button>
                      <button
                        type="button"
                        onClick={() => setSendTarget('contact')}
                        disabled={!canSendToContactSide || !selectedPlacement.contact?.email}
                        className={`text-xs px-2 py-2 rounded-md transition-colors ${
                          displayTarget === 'contact'
                            ? 'bg-purple-600 text-white shadow-sm'
                            : canSendToContactSide && selectedPlacement.contact?.email
                              ? 'text-foreground hover:bg-white'
                              : 'text-muted-foreground/50 cursor-not-allowed'
                        }`}
                        data-testid="send-target-contact"
                        title={
                          !selectedPlacement.contact?.email ? 'No contact email on this placement'
                            : !canSendToContactSide ? "This packet isn't set up to send to the contact"
                              : undefined
                        }
                      >
                        Contact
                      </button>
                      <button
                        type="button"
                        onClick={() => setSendTarget('both')}
                        disabled={!canSendToBothSides}
                        className={`text-xs px-2 py-2 rounded-md transition-colors ${
                          displayTarget === 'both'
                            ? 'bg-purple-600 text-white shadow-sm'
                            : canSendToBothSides
                              ? 'text-foreground hover:bg-white'
                              : 'text-muted-foreground/50 cursor-not-allowed'
                        }`}
                        data-testid="send-target-both"
                        title={!canSendToBothSides ? "This packet only has documents for one side, so there's nothing to send to both" : undefined}
                      >
                        Both
                      </button>
                    </div>
                  </div>

                  {senderFieldsPanel}
                  <Button
                    variant="outline"
                    className="w-full"
                    disabled={sending || previewing}
                    onClick={handleTestPreview}
                    data-testid="test-preview-button"
                    title="Dry-run the packet: render each PDF with prefilled values from this placement, without creating documents or sending emails."
                  >
                    {previewing ? (
                      <><RefreshCw className="w-4 h-4 animate-spin mr-2" /> Rendering preview…</>
                    ) : (
                      <><Eye className="w-4 h-4 mr-2" /> Test preview (no send)</>
                    )}
                  </Button>
                  <Button
                    className="w-full btn-primary"
                    disabled={sending || previewing || unfilledSenderFields.length > 0 || placementDeliverableCount === 0}
                    onClick={handleSend}
                    data-testid="send-packet-button"
                    title={
                      unfilledSenderFields.length > 0 ? 'Fill in the required fields above before sending'
                        : placementDeliverableCount === 0 ? 'No documents are deliverable to the selected recipient for this "Send to" choice'
                          : undefined
                    }
                  >
                    {sending ? (
                      <><RefreshCw className="w-4 h-4 animate-spin mr-2" /> Sending…</>
                    ) : (
                      <><Send className="w-4 h-4 mr-2" /> Send Packet ({placementDeliverableCount} document{placementDeliverableCount === 1 ? '' : 's'})</>
                    )}
                  </Button>
                </div>
              ) : selectedRecipient ? (
                <div className="space-y-4">
                  <div className="bg-muted/30 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">
                          {selectedRecipient.name || `${selectedRecipient.firstName || ''} ${selectedRecipient.lastName || ''}`.trim()}
                        </p>
                        <p className="text-sm text-muted-foreground">{selectedRecipient.email}</p>
                        {selectedRecipient.occupation && (
                          <p className="text-xs text-muted-foreground mt-1">{selectedRecipient.occupation}</p>
                        )}
                      </div>
                      <Button variant="outline" size="sm" onClick={() => setSelectedRecipient(null)}>
                        Change
                      </Button>
                    </div>
                  </div>

                  {/* Routing preview — every document in a candidate/contact
                      (non-placement) packet goes to the same recipient, but
                      show it explicitly per document rather than making the
                      sender infer it. */}
                  {selectedPacket.templates?.length > 0 && (
                    <div className="bg-muted/30 border border-border rounded-lg p-4" data-testid="recipient-routing-preview">
                      <p className="text-xs font-semibold text-foreground mb-1.5">Routing preview:</p>
                      <ul className="space-y-1 text-xs">
                        {selectedPacket.templates.map((t) => (
                          <li key={t.id} className="flex items-center gap-1.5">
                            {selectedPacket.entity_type === 'contact' ? (
                              <Building2 className="w-3 h-3 text-[#F9A825]" />
                            ) : (
                              <User className="w-3 h-3 text-[#4CAF50]" />
                            )}
                            <span className="truncate">{t.name}</span>
                            <span className="ml-auto text-muted-foreground">
                              to {selectedPacket.entity_type}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {senderFieldsPanel}
                  <Button
                    variant="outline"
                    className="w-full"
                    disabled={sending || previewing}
                    onClick={handleTestPreview}
                    data-testid="test-preview-button-recipient"
                    title="Dry-run the packet: render each PDF with prefilled values for this recipient, without creating documents or sending emails."
                  >
                    {previewing ? (
                      <><RefreshCw className="w-4 h-4 animate-spin mr-2" /> Rendering preview…</>
                    ) : (
                      <><Eye className="w-4 h-4 mr-2" /> Test preview (no send)</>
                    )}
                  </Button>
                  <Button
                    className="w-full btn-primary"
                    disabled={sending || previewing || unfilledSenderFields.length > 0}
                    onClick={handleSend}
                    data-testid="send-packet-button"
                    title={unfilledSenderFields.length > 0 ? 'Fill in the required fields above before sending' : undefined}
                  >
                    {sending ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send Packet ({selectedPacket.templates?.length || 0} documents)
                      </>
                    )}
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      value={searchQuery}
                      onChange={handleSearchChange}
                      placeholder={
                        selectedPacket.entity_type === 'placement'
                          ? 'Search placements by ID, candidate name or job title…'
                          : `Search ${selectedPacket.entity_type}s by name or email...`
                      }
                      className="pl-9"
                      data-testid="recipient-search"
                    />
                  </div>

                  {searching && (
                    <div className="flex items-center justify-center py-4">
                      <RefreshCw className="w-5 h-5 animate-spin text-primary" />
                    </div>
                  )}

                  {searchResults.length > 0 && (
                    <div className="border rounded-lg divide-y max-h-64 overflow-y-auto">
                      {searchResults.map(result => {
                        if (selectedPacket.entity_type === 'placement') {
                          return (
                            <div
                              key={result.id}
                              className="p-3 hover:bg-muted/50 cursor-pointer transition-colors"
                              onClick={() => handlePlacementSelect(result)}
                              data-testid={`placement-result-${result.id}`}
                            >
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full flex items-center justify-center bg-purple-100">
                                  <Package className="w-4 h-4 text-purple-600" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-sm truncate">
                                    {result.jobOrder?.title || `Placement #${result.id}`}
                                  </p>
                                  <p className="text-xs text-muted-foreground truncate">
                                    {result.candidate?.name || '—'}
                                    {result.company?.name && ` · ${result.company.name}`}
                                  </p>
                                </div>
                                <span className="text-xs text-muted-foreground">{result.status}</span>
                              </div>
                            </div>
                          );
                        }
                        return (
                          <div
                            key={result.id}
                            className="p-3 hover:bg-muted/50 cursor-pointer transition-colors"
                            onClick={() => handleRecipientSelect(result)}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                selectedPacket.entity_type === 'contact' ? 'bg-[#F9A825]/10' : 'bg-[#4CAF50]/10'
                              }`}>
                                {selectedPacket.entity_type === 'contact' ? (
                                  <Building2 className="w-4 h-4 text-[#F9A825]" />
                                ) : (
                                  <User className="w-4 h-4 text-[#4CAF50]" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm truncate">
                                  {result.name || `${result.firstName || ''} ${result.lastName || ''}`.trim()}
                                </p>
                                <p className="text-xs text-muted-foreground truncate">{result.email}</p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {searchQuery.length >= 2 && !searching && searchResults.length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No {selectedPacket.entity_type}s found
                    </p>
                  )}

                  {/* Manual entry — a placement packet is routed by a real
                      Bullhorn placement, so this is candidate/contact only. */}
                  {selectedPacket.entity_type !== 'placement' && (
                    <>
                      <div className="flex items-center gap-4">
                        <div className="flex-1 h-px bg-border" />
                        <span className="text-sm text-muted-foreground">or enter manually</span>
                        <div className="flex-1 h-px bg-border" />
                      </div>
                      <div className="space-y-3" data-testid="manual-recipient">
                        <div className="space-y-1.5">
                          <Label>Full Name</Label>
                          <Input
                            value={manualName}
                            onChange={(e) => setManualName(e.target.value)}
                            placeholder="John Doe"
                            data-testid="manual-recipient-name"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label>Email Address</Label>
                          <Input
                            type="email"
                            value={manualEmail}
                            onChange={(e) => setManualEmail(e.target.value)}
                            placeholder="john@example.com"
                            data-testid="manual-recipient-email"
                          />
                          {manualEmail.trim() && !manualEmailValid && (
                            <p className="text-xs text-destructive">Enter a valid email address</p>
                          )}
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          className="w-full"
                          disabled={!manualName.trim() || !manualEmailValid}
                          onClick={handleManualRecipient}
                          data-testid="manual-recipient-use"
                        >
                          Use this recipient
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

      </div>

      {/* "Preview all documents" — opens once a test preview renders. Left:
          every document in the packet with its recipient label; right: the
          actual rendered template (real PDF, mapped values overlaid) for
          whichever one is selected, not a bare download link. */}
      <Dialog open={previewModalOpen} onOpenChange={setPreviewModalOpen}>
        <DialogContent className="max-w-4xl w-[95vw] h-[85vh] flex flex-col" data-testid="packet-preview-modal">
          <DialogHeader>
            <DialogTitle>Preview all documents</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground -mt-2">
            Dry-run PDFs — no documents were created and no emails were sent.
          </p>
          <div className="flex flex-1 min-h-0 gap-4">
            <div className="w-56 flex-shrink-0 overflow-y-auto space-y-1 pr-2 border-r">
              {(previewResults || []).map((p, i) => {
                const pct = p.total_field_count > 0 ? (p.prefilled_count / p.total_field_count) * 100 : 0;
                const tone =
                  pct >= 80 ? 'text-emerald-700 bg-emerald-50 border-emerald-200' :
                  pct >= 40 ? 'text-amber-700 bg-amber-50 border-amber-200' :
                              'text-red-700 bg-red-50 border-red-200';
                return (
                  <button
                    key={p.template_id}
                    type="button"
                    onClick={() => setSelectedPreviewIndex(i)}
                    className={`w-full text-left p-2.5 rounded-lg text-sm border transition-colors ${
                      i === selectedPreviewIndex ? 'bg-primary/10 border-primary/40' : 'border-transparent hover:bg-muted/50'
                    }`}
                    data-testid={`preview-doc-${p.template_id}`}
                  >
                    <p className="font-medium truncate">{p.template_name}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      to {p.route}{p.recipient_name && <> · {p.recipient_name}</>}
                    </p>
                    {p.preview_url ? (
                      <span className={`inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded-full border font-medium ${tone}`}>
                        {p.prefilled_count} / {p.total_field_count} fields prefilled
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 mt-1 text-[10px] text-red-600">
                        <AlertCircle className="w-3 h-3" /> Render failed
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            <div className="flex-1 min-w-0 flex flex-col">
              {previewResults?.[selectedPreviewIndex]?.preview_url ? (
                <>
                  <embed
                    src={resolveBackendUrl(previewResults[selectedPreviewIndex].preview_url)}
                    type="application/pdf"
                    className="flex-1 w-full rounded-lg border"
                  />
                  <a
                    href={resolveBackendUrl(previewResults[selectedPreviewIndex].preview_url)}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-primary hover:underline mt-2 inline-flex items-center gap-1 self-start"
                  >
                    <ExternalLink className="w-3 h-3" /> Open in new tab
                  </a>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-sm text-red-600">
                  This document failed to render.
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <SignatureCaptureDialog
        open={!!signingKey}
        onOpenChange={(open) => !open && setSigningKey(null)}
        mode={signatureMode}
        onModeChange={setSignatureMode}
        typedValue={typedSignature}
        onTypedValueChange={setTypedSignature}
        canvasRef={initSignatureCanvas}
        onClearCanvas={() => signaturePadRef.current?.clear()}
        onCancel={() => setSigningKey(null)}
        onSave={saveSignature}
        testIds={{ dialog: 'packet-signature-dialog', canvas: 'packet-signature-canvas', save: 'packet-signature-save-btn' }}
      />
    </DashboardLayout>
  );
}
